/**
 * Autenticación y rol del usuario.
 *
 * Con Firebase configurado usa Firebase Auth con correo y contraseña, y lee
 * el perfil desde la colección `usuarios`, donde vive el rol. Sin configurar,
 * la app sigue funcionando en modo local — pero lo dice claramente en vez de
 * fingir que hay una sesión.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  type User,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, COL, SIN_CONFIGURAR } from '@/db/firebase';

// ── Roles ─────────────────────────────────────────────────────────────────

export type Rol =
  'ADMIN' | 'JEFE_CONTRATO' | 'JEFE_PLANTA' | 'OPERADOR_PLANTA' | 'CALIDAD' | 'RRHH' | 'LECTOR';

export const NOMBRE_ROL: Record<Rol, string> = {
  ADMIN: 'Administrador',
  JEFE_CONTRATO: 'Jefe de contrato',
  JEFE_PLANTA: 'Jefe de planta',
  OPERADOR_PLANTA: 'Operador de planta',
  CALIDAD: 'Calidad',
  RRHH: 'Recursos humanos',
  LECTOR: 'Solo lectura',
};

/** Qué puede hacer cada rol. La UI oculta; las reglas de Firestore impiden. */
export type Permiso =
  | 'verDashboard'
  | 'crearGuia'
  | 'anularGuia'
  | 'editarFacturada'
  | 'catalogos'
  | 'contrato'
  | 'estadoPago'
  | 'jornadaPropia'
  | 'jornadaTerceros'
  | 'reportes'
  | 'administrar';

const PERMISOS: Record<Rol, Permiso[]> = {
  ADMIN: [
    'verDashboard',
    'crearGuia',
    'anularGuia',
    'editarFacturada',
    'catalogos',
    'contrato',
    'estadoPago',
    'jornadaPropia',
    'jornadaTerceros',
    'reportes',
    'administrar',
  ],
  JEFE_CONTRATO: [
    'verDashboard',
    'crearGuia',
    'anularGuia',
    'catalogos',
    'contrato',
    'estadoPago',
    'jornadaPropia',
    'reportes',
  ],
  JEFE_PLANTA: [
    'verDashboard',
    'crearGuia',
    'anularGuia',
    'jornadaPropia',
    'jornadaTerceros',
    'reportes',
  ],
  OPERADOR_PLANTA: ['verDashboard', 'crearGuia', 'jornadaPropia'],
  CALIDAD: ['verDashboard', 'catalogos', 'jornadaPropia', 'reportes'],
  RRHH: ['jornadaPropia', 'jornadaTerceros', 'reportes'],
  LECTOR: ['verDashboard', 'reportes'],
};

export interface Perfil {
  uid: string;
  email: string;
  nombre: string;
  rol: Rol;
  plantaId: string | null;
  activo: boolean;
  /**
   * No aparece en la lista de usuarios que ven los demás administradores.
   *
   * Es para la dueña o el dueño del contrato: quien administra el día a día
   * necesita ver a su gente, pero no tiene por qué ver —ni poder tocar— la
   * cuenta de quien está por encima. Quien está oculto sí ve a todo el mundo,
   * incluidos los demás ocultos.
   *
   * No es cosmético: las reglas de Firestore niegan la lectura del perfil
   * oculto a quien no lo sea, así que tampoco se alcanza por otra vía.
   */
  oculto?: boolean;
}

interface Contexto {
  /** `true` mientras se resuelve la sesión inicial. */
  cargando: boolean;
  /** Sin proyecto Firebase: la app corre abierta en este dispositivo. */
  modoLocal: boolean;
  usuario: User | null;
  perfil: Perfil | null;
  /** Hay con quién trabajar: sesión iniciada, o modo local. */
  dentro: boolean;
  error: string | null;
  entrar: (email: string, clave: string) => Promise<void>;
  salir: () => Promise<void>;
  recuperarClave: (email: string) => Promise<void>;
  puede: (p: Permiso) => boolean;
}

const Ctx = createContext<Contexto>({
  cargando: true,
  modoLocal: true,
  usuario: null,
  perfil: null,
  dentro: false,
  error: null,
  entrar: async () => {},
  salir: async () => {},
  recuperarClave: async () => {},
  puede: () => true,
});

/** Traduce los códigos de Firebase a algo que se entienda. */
function mensajeDeError(e: unknown): string {
  const codigo = (e as { code?: string })?.code ?? '';
  switch (codigo) {
    case 'auth/invalid-email':
      return 'El correo no tiene un formato válido.';
    case 'auth/user-disabled':
      return 'Esta cuenta está desactivada. Habla con el administrador.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Correo o contraseña incorrectos.';
    case 'auth/too-many-requests':
      return 'Demasiados intentos fallidos. Espera unos minutos e inténtalo de nuevo.';
    case 'auth/network-request-failed':
      return 'Sin conexión. Revisa la red e inténtalo de nuevo.';
    default:
      return e instanceof Error ? e.message : 'No se pudo iniciar sesión.';
  }
}

export function ProveedorAuth({ children }: { children: ReactNode }) {
  const modoLocal = SIN_CONFIGURAR || !auth;

  const [cargando, setCargando] = useState(!modoLocal);
  const [usuario, setUsuario] = useState<User | null>(null);
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (modoLocal || !auth) return;

    return onAuthStateChanged(auth, (u) => {
      void (async () => {
        setUsuario(u);
        if (!u) {
          setPerfil(null);
          setCargando(false);
          return;
        }
        try {
          const ref = doc(db!, COL.usuarios, u.uid);
          const snap = await getDoc(ref);

          if (snap.exists()) {
            const d = snap.data() as Partial<Perfil>;
            setPerfil({
              uid: u.uid,
              email: u.email ?? '',
              nombre: d.nombre ?? u.displayName ?? u.email ?? '',
              rol: (d.rol as Rol) ?? 'LECTOR',
              plantaId: d.plantaId ?? null,
              activo: d.activo ?? true,
              oculto: d.oculto ?? false,
            });
          } else {
            // Primer ingreso sin perfil: entra como lector hasta que un
            // administrador le asigne rol. Nunca se auto-asigna ADMIN.
            const nuevo: Perfil = {
              uid: u.uid,
              email: u.email ?? '',
              nombre: u.displayName ?? u.email ?? '',
              rol: 'LECTOR',
              plantaId: null,
              activo: true,
              oculto: false,
            };
            await setDoc(ref, { ...nuevo, creadoEn: new Date().toISOString() }, { merge: true });
            setPerfil(nuevo);
          }
        } catch (e) {
          console.error('No se pudo leer el perfil', e);
          setError('Se inició sesión, pero no se pudo leer el perfil del usuario.');
        } finally {
          setCargando(false);
        }
      })();
    });
  }, [modoLocal]);

  const entrar = useCallback(async (email: string, clave: string) => {
    if (!auth) throw new Error('Firebase no está configurado.');
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), clave);
    } catch (e) {
      const msg = mensajeDeError(e);
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const salir = useCallback(async () => {
    if (auth) await signOut(auth);
    setPerfil(null);
  }, []);

  const recuperarClave = useCallback(async (email: string) => {
    if (!auth) throw new Error('Firebase no está configurado.');
    try {
      await sendPasswordResetEmail(auth, email.trim());
    } catch (e) {
      throw new Error(mensajeDeError(e));
    }
  }, []);

  const puede = useCallback(
    (p: Permiso) => {
      // Sin Firebase no hay a quién restringir: la app queda abierta, y eso
      // se avisa en pantalla en vez de simular permisos.
      if (modoLocal) return true;
      if (!perfil?.activo) return false;
      return PERMISOS[perfil.rol].includes(p);
    },
    [modoLocal, perfil],
  );

  const valor = useMemo<Contexto>(
    () => ({
      cargando,
      modoLocal,
      usuario,
      perfil,
      dentro: modoLocal || Boolean(usuario && perfil?.activo),
      error,
      entrar,
      salir,
      recuperarClave,
      puede,
    }),
    [cargando, modoLocal, usuario, perfil, error, entrar, salir, recuperarClave, puede],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useAuth() {
  return useContext(Ctx);
}
